import type { RawContentUnit } from './useComponent';

// Resolving the <img src> for an IMAGE content unit.
//
// A unit carries two URLs: `image_url` (the raw, full-resolution Firebase
// download URL) and `dynamic_image_url` (an App Engine Images serving URL on
// lh3.googleusercontent.com that resizes on the fly when a `=sN` suffix is
// appended). Requesting the serving URL WITHOUT a suffix returns Google's small
// default rendition — which is why images looked low quality. So when a dynamic
// serving URL is present we always append an explicit size suffix; the raw
// Firebase URL is never suffixed (that would break it).

function asString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return false;
}

/**
 * The Google serving-URL size suffix for an image unit's authored size:
 *   small → `=s720`, medium → `=s1080`, large → `=s1440`.
 * The suffix caps the longest dimension (in pixels), so higher values render
 * sharper — especially on large layouts and high-DPR screens. When no size is
 * authored, request the original with `=s0` so the image is never served at
 * Google's small default rendition.
 */
export function getImageSizeSuffix(unit: RawContentUnit): string {
  const rawSize = unit.image_size ?? unit.imageSize;

  if (typeof rawSize === 'number') {
    if (rawSize === 1) {
      return '=s720';
    }
    if (rawSize === 2) {
      return '=s1080';
    }
    if (rawSize === 3) {
      return '=s1440';
    }
    // 0 / unknown → treated as no explicit size below.
  } else {
    const size = asString(rawSize)?.toUpperCase();
    if (size === '1' || size === 'SMALL') {
      return '=s720';
    }
    if (size === '2' || size === 'MEDIUM') {
      return '=s1080';
    }
    if (size === '3' || size === 'LARGE') {
      return '=s1440';
    }
    // '0' / 'DEFAULT' / unset → no explicit size.
  }

  // A legacy "small image" flag with no explicit size still means small.
  if (asBoolean(unit.use_small_image ?? unit.useSmallImage)) {
    return '=s720';
  }

  // No authored size: serve the original.
  return '=s0';
}

/**
 * The `<img src>` for an image content unit. When a resizable dynamic serving
 * URL is present, append the size suffix (replacing any existing `=…` suffix so
 * we never produce `…=s1080=s0`); otherwise fall back to the raw image_url
 * unchanged. Returns null when the unit has no usable source.
 */
export function resolveImageUnitSrc(unit: RawContentUnit): string | null {
  const dynamicUrl = asString(unit.dynamic_image_url ?? unit.dynamicImageUrl);
  if (dynamicUrl) {
    return stripSizeSuffix(dynamicUrl) + getImageSizeSuffix(unit);
  }
  return asString(unit.image_url ?? unit.imageUrl);
}

// --- re-resolving at a different displayed size ------------------------------
//
// A reader can display an image at a size other than the authored one (see the
// student-image-size capability). The authored size is baked into `src` during
// normalization, so enlarging would CSS-upscale a small file — the readers most
// likely to enlarge are exactly the ones trying to read fine detail. These
// helpers re-derive the source from the size actually being displayed.

export type ImageDisplaySize = 'large' | 'medium' | 'small';

/**
 * Suffixes ordered by how many pixels they yield, LOWEST first.
 *
 * `=s0` is last because it means the uncapped original, so it outranks every
 * explicit cap. Comparing the number inside the suffix would sort it lowest and
 * silently downgrade every image with no authored size — which is most older
 * content.
 */
const SUFFIXES_BY_RESOLUTION = ['=s720', '=s1080', '=s1440', '=s0'] as const;

function resolutionRank(suffix: string): number {
  const rank = SUFFIXES_BY_RESOLUTION.indexOf(
    suffix as (typeof SUFFIXES_BY_RESOLUTION)[number],
  );
  // An unrecognized suffix is treated as the lowest rank so it never wins
  // against a known one.
  return rank === -1 ? -1 : rank;
}

function stripSizeSuffix(url: string): string {
  return url.replace(/=[-\w]+$/, '');
}

/** The serving-URL suffix for a size a reader can choose. */
export function suffixForDisplaySize(size: ImageDisplaySize): string {
  if (size === 'small') {
    return '=s720';
  }
  if (size === 'large') {
    return '=s1440';
  }
  return '=s1080';
}

/**
 * The higher-resolution of two suffixes.
 *
 * Resolution must only ever increase. Displaying an image SMALLER than it was
 * authored keeps the authored suffix: re-requesting a lower resolution would
 * spend a network round trip to obtain a worse image than the one already
 * decoded and cached, and would visibly degrade the figure for a reader who was
 * only tidying their view.
 */
export function higherResolutionSuffix(a: string, b: string): string {
  return resolutionRank(b) > resolutionRank(a) ? b : a;
}

/**
 * What a resizable image needs in order to be re-resolved at another size.
 *
 * The authored SUFFIX is carried rather than the authored size, because the two
 * are not interchangeable: an unset size normalizes to `medium` for layout but
 * to `=s0` for resolution, so `medium` alone cannot say whether the original was
 * requested or a 1080px cap was.
 */
export interface ResizableImageSource {
  authoredSuffix: string;
  servingUrlBase: string;
}

/**
 * The re-resolution inputs for a unit, or null when it has no resizable serving
 * URL. A raw storage URL cannot be re-requested at another resolution and must
 * never be suffixed.
 */
export function getResizableImageSource(
  unit: RawContentUnit,
): ResizableImageSource | null {
  const dynamicUrl = asString(unit.dynamic_image_url ?? unit.dynamicImageUrl);

  if (!dynamicUrl) {
    return null;
  }

  return {
    authoredSuffix: getImageSizeSuffix(unit),
    servingUrlBase: stripSizeSuffix(dynamicUrl),
  };
}

/**
 * The `<img src>` to use when displaying an image at `displaySize`.
 *
 * Returns `src` unchanged when the image has no resizable serving URL — the
 * displayed size still changes, it just cannot get sharper.
 */
export function resolveImageSrcForDisplaySize(
  src: string,
  resizable: ResizableImageSource | null | undefined,
  displaySize: ImageDisplaySize,
): string {
  if (!resizable) {
    return src;
  }

  return (
    resizable.servingUrlBase +
    higherResolutionSuffix(
      resizable.authoredSuffix,
      suffixForDisplaySize(displaySize),
    )
  );
}
